import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useI18n } from '../../i18n/i18nService';
import ThemeToggle from '../ThemeToggle';
import menuItems from './menu';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

function Sidebar({ currentPagePath, isSidebarOpen, setIsSidebarOpen }) {
  const { t } = useI18n();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});

  const isActive = (path, submenus = []) => {
    const currentPath = location.pathname;
    return currentPath === path || submenus.some(sub => currentPath.startsWith(sub.path));
  };

  // Un seul menu ouvert à la fois
  const toggleMenu = (menuName) => {
    setExpandedMenus(prev => {
      const isOpen = prev[menuName];
      return isOpen ? {} : { [menuName]: true };
    });
  };

  return (
    <aside
      className={`fixed top-0 left-0 z-40 h-screen w-64 transition-transform transform 
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
        md:relative md:translate-x-0 bg-light-surface dark:bg-dark-surface border-r border-light-tertiary/20 dark:border-dark-tertiary/20 flex flex-col`}
    >
      {/* Header mobile pour fermeture */}
      <div className="md:hidden p-4 flex justify-between items-center border-b border-light-tertiary/10 dark:border-dark-tertiary/10">
        <span className="text-lg font-semibold text-light-primary dark:text-dark-primary">IDARAF</span>
        <button onClick={() => setIsSidebarOpen(false)}>
          <XMarkIcon className="h-6 w-6 text-light-text dark:text-dark-text" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-4">
        <ul className="space-y-1 px-2">
          {menuItems.map((item) => (
            <li key={item.name}>
              {!item.submenus ? (
                <Link
                  to={item.path}
                  onClick={() => setIsSidebarOpen(false)}
                  className={`block px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-light-primary/10 dark:bg-dark-primary/10 text-light-primary dark:text-dark-primary'
                      : 'text-light-text dark:text-dark-text hover:bg-light-primary/5 dark:hover:bg-dark-primary/5'
                  }`}
                >
                  <div className="flex items-center">
                    <span className="text-light-primary dark:text-dark-primary">
                      {item.icon}
                    </span>
                    <span className="ml-3 text-sm font-medium">{t(item.name)}</span>
                  </div>
                </Link>
              ) : (
                <button
                  onClick={() => toggleMenu(item.name)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                    isActive(item.path, item.submenus)
                      ? 'bg-light-primary/10 dark:bg-dark-primary/10 text-light-primary dark:text-dark-primary'
                      : 'text-light-text dark:text-dark-text hover:bg-light-primary/5 dark:hover:bg-dark-primary/5'
                  }`}
                >
                  <div className="flex items-center">
                    <span className="text-light-primary dark:text-dark-primary">
                      {item.icon}
                    </span>
                    <span className="ml-3 text-sm font-medium">{t(item.name)}</span>
                  </div>
                  <span className="text-light-tertiary dark:text-dark-tertiary">
                    {expandedMenus[item.name] ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" />
                    )}
                  </span>
                </button>
              )}
              {/* Sous-menus */}
              {item.submenus && expandedMenus[item.name] && (
                <ul className="ml-10 mt-1 space-y-1">
                  {item.submenus.map((sub) => (
                    <li key={sub.name}>
                      <Link
                        to={sub.path}
                        onClick={() => setIsSidebarOpen(false)}
                        className={`block px-3 py-2 text-sm rounded-md transition-colors ${
                          location.pathname === sub.path
                            ? 'text-light-primary dark:text-dark-primary font-medium'
                            : 'text-light-tertiary dark:text-dark-tertiary hover:text-light-text dark:hover:text-dark-text'
                        }`}
                      >
                        {t(sub.name)}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* Mode sombre en bas */}
      <div className="p-4 border-t border-light-tertiary/10 dark:border-dark-tertiary/10">
        <ThemeToggle mobile />
      </div>
    </aside>
  );
}

export default Sidebar;
